import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CollectionLight } from '../../../../models/collections/collection-light.model';
import { CollectionPeriod } from '../../../../models/collections/collection-period.model';
import { ApiCollectionsService } from '../../../../services/collections/api/api-collections.service';
import { AlertsService } from '../../../../services/alerts.service';
import { ModalsService } from '../../../../services/modals.service';
import { DateService } from '../../../../services/date.service';
import { CollectionPeriodAddModalComponent } from '../../modals/collection-period-add-modal/collection-period-add-modal.component';
import { CollectionPeriodEditModalComponent } from '../../modals/collection-period-edit-modal/collection-period-edit-modal.component';

@Component({
  selector: 'app-collections-periods-tab',
  templateUrl: './collections-periods-tab.component.html',
  standalone: true,
  imports: [CommonModule, TooltipModule, CollectionPeriodAddModalComponent, CollectionPeriodEditModalComponent]
})
export class CollectionsPeriodsTabComponent {

  @ViewChild('addModal') addModal!: CollectionPeriodAddModalComponent;
  @ViewChild('editModal') editModal!: CollectionPeriodEditModalComponent;

  periods: CollectionPeriod[] = [];
  collections: CollectionLight[] = [];
  isLoading: boolean = false;
  totalResults: number = 0;
  readonly resultsPerPage: number = 25;

  private static pageNumber: number = 1;

  get pageNumber(): number { return CollectionsPeriodsTabComponent.pageNumber; }
  get totalPages(): number { return Math.ceil(this.totalResults / this.resultsPerPage); }

  constructor(
    public dateService: DateService,
    private apiCollectionsService: ApiCollectionsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService
  ) {}

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  goToPage(page: number): void { CollectionsPeriodsTabComponent.pageNumber = page; this.refreshPeriods(); }
  onClickPaginationPrevious(): void { CollectionsPeriodsTabComponent.pageNumber--; this.refreshPeriods(); }
  onClickPaginationNext(): void { CollectionsPeriodsTabComponent.pageNumber++; this.refreshPeriods(); }

  getPeriodStatus(period: CollectionPeriod): 'waiting' | 'in_progress' | 'past' {
    const now = Math.floor(Date.now() / 1000);
    if (now < period.startDateTimestamp) return 'waiting';
    if (now > period.endDateTimestamp) return 'past';
    return 'in_progress';
  }

  getCollectionName(collectionId: number | null): string {
    if (collectionId === null) return '-';
    return this.collections.find(c => c.collectionId === collectionId)?.name ?? String(collectionId);
  }

  onClickAdd(): void {
    this.addModal.show(() => this.refreshPeriods());
  }

  onClickEdit(period: CollectionPeriod): void {
    this.editModal.show(period, () => this.refreshPeriods());
  }

  onClickDelete(period: CollectionPeriod): void {
    this.modalsService.showConfirmModal(() => {
      this.apiCollectionsService.deleteCollectionPeriod(period.collectionPeriodId).then(
        () => {
          this.alertsService.displayAlertSuccess('Period deleted !');
          this.refreshPeriods();
        }, error => {
          this.alertsService.displayAlertError('Error deleting period : ' + error);
        }
      );
    });
  }

  refresh(): void {
    this.refreshCollections();
    this.refreshPeriods();
  }

  private refreshCollections(): void {
    this.apiCollectionsService.getAllCollections().then(
      response => {
        this.collections = response.results;
      }, error => {
        this.alertsService.displayAlertError('Error loading collections : ' + error);
      }
    );
  }

  private refreshPeriods(): void {
    this.isLoading = true;
    this.apiCollectionsService.getCollectionsPeriods(this.pageNumber, this.resultsPerPage).then(
      response => {
        this.isLoading = false;
        this.periods = response.results;
        this.totalResults = response.resultsTotal;
        if (this.pageNumber > this.totalPages && this.totalPages > 0) {
          CollectionsPeriodsTabComponent.pageNumber = 1;
          this.refreshPeriods();
        }
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading periods : ' + error);
      }
    );
  }
}
